import { useGlobalToast } from 'transfix-ui/components/Toast'
import {
  ICreateConfigGroupInput,
  IUpdateConfigGroupInput,
  ICreateConfigGroupMappingInput,
  IUpdateConfigGroupMappingInput,
  IConfigGroup,
  Maybe,
} from 'types/graphqlTypes'
import { TEdiConfigGroupMappingSchema } from '../forms/EdiConfigGroupMappingSchema'
import { useCreateConfigGroupMutation } from '../graphql/CreateConfigGroup'
import { useCreateConfigGroupMappingMutation } from '../graphql/CreateConfigGroupMapping'
import { useUpdateConfigGroupMutation } from '../graphql/UpdateConfigGroup'
import { useUpdateConfigGroupMappingMutation } from '../graphql/UpdateConfigGroupMapping'

export const useEdiMessageTypeMutations = () => {
  const { openToast } = useGlobalToast()

  const [_createConfigGroup] = useCreateConfigGroupMutation()
  const [_updateConfigGroup] = useUpdateConfigGroupMutation()
  const [_createConfigGroupMapping] = useCreateConfigGroupMappingMutation()
  const [_updateConfigGroupMapping] = useUpdateConfigGroupMappingMutation()

  const createConfigGroup = async (input: ICreateConfigGroupInput) => {
    try {
      const response = await _createConfigGroup({
        variables: {
          input,
        },
        refetchQueries: ['ConfigGroupMappings'],
      })

      const { data } = response || {}
      const { createConfigGroup } = data || {}
      const { errors = [] } = createConfigGroup || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return { id: null }
      }

      openToast({
        toastMessage: 'Config Group created successfully.',
        toastType: 'default',
      })

      return createConfigGroup?.configGroup || { id: null }
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
      return { id: null }
    }
  }

  const updateConfigGroup = async (input: IUpdateConfigGroupInput) => {
    try {
      const response = await _updateConfigGroup({
        variables: {
          input,
        },
        refetchQueries: ['ConfigGroupMappings'],
      })

      const { data } = response || {}
      const { updateConfigGroup } = data || {}
      const { errors = [] } = updateConfigGroup || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Config Group updated successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  const createConfigGroupMapping = async (input: ICreateConfigGroupMappingInput) => {
    try {
      const response = await _createConfigGroupMapping({
        variables: {
          input,
        },
        refetchQueries: ['ConfigGroupMappings'],
      })

      const { data } = response || {}
      const { createConfigGroupMapping } = data || {}
      const { errors = [] } = createConfigGroupMapping || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Config Group Mapping created successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  const updateConfigGroupMapping = async (input: IUpdateConfigGroupMappingInput) => {
    try {
      const response = await _updateConfigGroupMapping({
        variables: {
          input,
        },
        refetchQueries: ['ConfigGroupMappings'],
      })

      const { data } = response || {}
      const { updateConfigGroupMapping } = data || {}
      const { errors = [] } = updateConfigGroupMapping || {}
      const filteredErrors = errors.filter(item => !!item.message).map(i => i.message)

      if (filteredErrors.length > 0) {
        openToast({
          toastMessage: filteredErrors.join(', '),
          toastType: 'alert',
          toastDuration: null,
        })
        return
      }

      openToast({
        toastMessage: 'Config Group Mapping updated successfully.',
        toastType: 'default',
      })
    } catch (e) {
      openToast({ toastMessage: e.message, toastType: 'alert' })
    }
  }

  const submitConfigGroupMapping = async (
    formParams: TEdiConfigGroupMappingSchema,
    connectionConfigId: string,
    shipperId: string,
    configGroups: Maybe<IConfigGroup>[] | undefined
  ) => {
    const configGroupNames = configGroups?.map(configGroup => configGroup?.name)
    if (formParams.configGroupId && formParams.configGroupMappingId) {
      updateConfigGroup({
        id: formParams.configGroupId,
        name: formParams.configGroupName,
      })

      updateConfigGroupMapping({
        id: formParams.configGroupMappingId,
        isaSenderId: formParams.isaSenderId,
      })
    } else if (!configGroupNames?.includes(formParams.configGroupName)) {
      // means we don't have an existing config group, create new one
      const newConfigGroup = await createConfigGroup({
        name: formParams.configGroupName,
        shipperApplicationId: shipperId,
      })

      if (newConfigGroup?.id) {
        createConfigGroupMapping({
          ediMapperConfigGroupId: newConfigGroup?.id,
          isaSenderId: formParams.isaSenderId,
          inboundEdiMapperConnectionConfigId: connectionConfigId,
        })
      }
    } else {
      // means found an existing config group, only create new mapping
      const configGroup = configGroups?.find(
        configGroup => configGroup?.name === formParams.configGroupName
      )
      if (configGroup) {
        createConfigGroupMapping({
          ediMapperConfigGroupId: configGroup?.id,
          isaSenderId: formParams.isaSenderId,
          inboundEdiMapperConnectionConfigId: connectionConfigId,
        })
      } else {
        openToast({
          toastMessage: 'Config Group Name must be one of existing config group names',
          toastType: 'alert',
          toastDuration: null,
        })
      }
    }
  }

  return { submitConfigGroupMapping }
}
